/* eslint-disable jsx-a11y/no-autofocus */
import React, { FC } from "react";
import { RoleFolderPermissionDto } from "../../types/Role";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Grid2,
  IconButton,
  Paper,
  Divider,
  InputAdornment,
  Alert,
  useMediaQuery,
} from "@mui/material";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import PermissionGrid from "./PermissionGrid";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import BackdropProgressBar from "../../components/ui/BackdropProgressBar";
import { UPLOADMESSAGES } from "../../constants/messages";
import theme from "../../assets/theme/theme";
import NotificationSettings from "../../components/ui/NotificationSettings";
import { NotificationType } from "../../data/Enums";

interface UploadUIProps {
  description: string;
  loading: boolean;
  validationError: string | null;
  dialogOpen: boolean;
  handleDiscard: () => void;
  handleDialogClose: (confirmed: boolean) => void;
  handleDescriptionChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  permissions: RoleFolderPermissionDto[] | null;
  handlePermissionChange: (
    index: number,
    field: keyof RoleFolderPermissionDto,
    value: boolean
  ) => void;
  handleUserSelectionChange: (roleId: number, selectedUserIds: number[]) => void;
  file: File | null;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  onModalClose: () => void;
  onNotificationChange: (selected: NotificationType[], notificationEnabled: boolean) => void;
}

const UploadUI: FC<UploadUIProps> = ({
  description,
  loading,
  validationError,
  dialogOpen,
  handleDiscard,
  handleDialogClose,
  handleDescriptionChange,
  permissions,
  handlePermissionChange,
  handleUserSelectionChange,
  file,
  onFileChange,
  onSubmit,
  onModalClose,
  onNotificationChange,
}) => {
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));

  return (
    <Dialog open fullWidth maxWidth="md" disableRestoreFocus>
      <DialogTitle>Create or Upload Folder/File</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onModalClose}
        sx={() => ({
          position: "absolute",
          right: 8,
          top: 3,
          color: "#fff",
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Grid2 container spacing={0}>
          {/* Create folder Column */}
          <Grid2 size={{ xs: 12, sm: 5.5 }}>
            <Paper
              sx={{
                padding: 1,
                borderRadius: "10px",
                backgroundColor: "#f9f9f9",
              }}
              variant="outlined"
            >
              <Typography variant="h5" mb={1}>
                Create Folder
              </Typography>
              <TextField
                fullWidth
                label="Folder name"
                variant="outlined"
                size="small"
                value={description}
                onChange={handleDescriptionChange}
                className="custom-textfield"
                sx={{
                  "& .MuiInputBase-root": { background: "#fff" },
                }}
                slotProps={{ htmlInput: { "data-testid": "txtFolderName" } }}
                InputProps={{
                  endAdornment: description ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => handleDescriptionChange(null)}>
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Paper>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 1 }}>
            <Divider
              orientation={isXs ? "horizontal" : "vertical"}
              flexItem
              sx={{
                width: isXs ? "100%" : "auto",
                height: isXs ? "auto" : "100%",
                mt: isXs ? 2 : 0,
                mb: isXs ? 1 : 0,
              }}
            >
              or
            </Divider>
          </Grid2>
          {/* Upload Column */}
          <Grid2 size={{ xs: 12, sm: 5.5 }}>
            <Paper
              sx={{
                padding: 1,
                backgroundColor: "#f9f9f9",
                borderRadius: "10px",
              }}
              variant="outlined"
            >
              <Typography variant="h5" mb={1}>
                Upload File
              </Typography>

              <TextField
                fullWidth
                value={file ? file.name : ""}
                placeholder="No file selected"
                size="small"
                className="custom-textfield"
                sx={{
                  "& .MuiInputBase-root": {
                    paddingRight: "6px",
                    background: "#fff",
                  },
                }}
                InputProps={{
                  readOnly: true, // Make input non-editable
                  endAdornment: (
                    <InputAdornment position="end">
                      {file && (
                        <IconButton size="small" onClick={() => onFileChange(null)}>
                          <CloseIcon />
                        </IconButton>
                      )}
                      <Button
                        size="small"
                        component="label"
                        variant="contained"
                        color="secondary"
                        startIcon={<CloudUploadIcon />}
                      >
                        Upload
                        <input type="file" hidden onChange={onFileChange} />
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Paper>
          </Grid2>
        </Grid2>

        {<BackdropProgressBar loading={loading} />}
        {validationError && (
          <Alert severity="error" sx={{ mt: "5px", mb: "5px" }}>
            {validationError}
          </Alert>
        )}
        <Box mt={1.5}>
          <PermissionGrid
            permissions={permissions ?? []}
            onPermissionChange={handlePermissionChange}
            onUserChange={handleUserSelectionChange}
            columnVisibilityModel={{ roleId: false }}
            disableAll={false}
          />
        </Box>
        <Box mt={0.5}>
          <NotificationSettings onChange={onNotificationChange} />
        </Box>
      </DialogContent>

      <ConfirmationDialog
        open={dialogOpen}
        title={
          file !== null ? UPLOADMESSAGES.FILE_PUBLISH_TITLE : UPLOADMESSAGES.FOLDER_PUBLISH_TITLE
        }
        message={
          file !== null
            ? UPLOADMESSAGES.FILE_PUBLISH_CONFIRM
            : UPLOADMESSAGES.FOLDER_PUBLISH_CONFIRM
        }
        onClose={handleDialogClose}
        onDiscard={handleDiscard}
      />

      <DialogActions>
        <Button variant="contained" color="light" onClick={onModalClose}>
          Cancel
        </Button>
        <Button id="Confirm" variant="contained" color="primary" autoFocus onClick={onSubmit}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadUI;
